import type { HomepageLayoutContent, HomepageLayoutSection } from '../components/sections/sectionRegistry';
import { isCompositionSection } from './sectionLayoutMode';

export function isImmersiveCanvasSection(section: Pick<HomepageLayoutSection, 'type' | 'settings'> | null | undefined) {
  return (
    section?.type === 'canvas' &&
    section.settings?.canvas?.presentation === 'immersive' &&
    isCompositionSection(section)
  );
}

export function getStageSection(layout: Pick<HomepageLayoutContent, 'sections'> | null | undefined) {
  const sections = Array.isArray(layout?.sections) ? layout.sections : [];
  return sections.find(isImmersiveCanvasSection) ?? null;
}

export function getStageViewSection(
  layout: Pick<HomepageLayoutContent, 'sections'> | null | undefined,
  selectedSection?: Pick<HomepageLayoutSection, 'type' | 'settings'> | null
) {
  const sections = Array.isArray(layout?.sections) ? layout.sections : [];
  const strictStageSection = sections.find(isImmersiveCanvasSection);
  if (strictStageSection) return strictStageSection;

  return isImmersiveCanvasSection(selectedSection) ? selectedSection as HomepageLayoutSection : null;
}

export function isStagePageLayout(layout: Pick<HomepageLayoutContent, 'sections'> | null | undefined) {
  return Boolean(getStageSection(layout));
}

export function isStrictStagePageLayout(layout: Pick<HomepageLayoutContent, 'sections'> | null | undefined) {
  const sections = Array.isArray(layout?.sections) ? layout.sections : [];
  return sections.length === 1 && isImmersiveCanvasSection(sections[0]);
}

export function resolveStagePageSections<T extends Pick<HomepageLayoutSection, 'type' | 'settings'>>(
  sections: T[] | null | undefined
): T[] {
  const normalizedSections = Array.isArray(sections) ? sections : [];
  const stageSection = normalizedSections.find(isImmersiveCanvasSection);
  return stageSection ? [stageSection] : normalizedSections;
}

export function canAddSectionToLayout(
  layout: Pick<HomepageLayoutContent, 'sections'> | null | undefined,
  nextSection: Pick<HomepageLayoutSection, 'type' | 'settings'> | null | undefined
) {
  const sections = Array.isArray(layout?.sections) ? layout.sections : [];
  const containsStageSection = sections.some(isImmersiveCanvasSection);
  const nextIsStageSection = isImmersiveCanvasSection(nextSection);

  if (containsStageSection) {
    return {
      allowed: false,
      reason: 'Stage pages can only contain one fullscreen section.',
    };
  }

  if (nextIsStageSection && sections.length > 0) {
    return {
      allowed: false,
      reason: 'Add fullscreen sections to an empty page. Stage pages cannot contain other sections.',
    };
  }

  return { allowed: true, reason: null };
}
